import { IconInfoCircle } from "@tabler/icons-react";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { TOOLTIP_Z_DEFAULT, TooltipLayerContext, useTooltipZ } from "./tooltipLayer";

/**
 * Hands every hint below it a rung (see `tooltipLayer.ts`): a surface that
 * sits above the default hint rung wraps its content in this, so a hint of
 * its own paints ABOVE it instead of under it (audit hop3 r2 M1 / L3).
 *
 * Always the same Provider (re-audit r3 L1): switching between a Fragment
 * and a Provider when a host's z crosses the rung would remount its whole
 * subtree — drafts, input and focus lost. A surface at or below the rung
 * (`z` undefined) starts its subtree at the DEFAULT rung (r3 L2): a low
 * dialog never inherits the rung of a raised popup it was opened from.
 */
export function TooltipLayer({ z, children }: { z: number | undefined; children: React.ReactNode }) {
  return <TooltipLayerContext.Provider value={z ?? TOOLTIP_Z_DEFAULT}>{children}</TooltipLayerContext.Provider>;
}

/**
 * A small picture above the sentence (board 01.11., L-26) — the GIFs in
 * `src/assets/hints`. Fixed size, so the bubble is laid out (and clamped)
 * before the file has loaded.
 */
export type PInfoMedia = {
  /** The moving picture. */
  src: string;
  /** One still of it: what `prefers-reduced-motion` gets instead. */
  poster: string;
  width: number;
  height: number;
};

type PInfoProps = {
  message: string;
  position: { top: number; left: number; showAbove?: boolean };
  /** Lets a trigger name this bubble in `aria-describedby`. */
  id?: string;
  media?: PInfoMedia;
};

const VIEWPORT_MARGIN = 8;

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Decoration: `alt=""` + `aria-hidden` — the sentence under it is the
 * description, and a screen reader must not read the hint twice. Mounted
 * only with an open bubble, so the file loads on open, not with the page.
 */
function BubbleMedia({ media }: { media: PInfoMedia }) {
  const still = useMediaQuery(REDUCED_MOTION_QUERY);
  return (
    <img
      src={still ? media.poster : media.src}
      width={media.width}
      height={media.height}
      alt=""
      aria-hidden="true"
      draggable={false}
      className="block mb-2 rounded"
      style={{ width: media.width, height: media.height }}
    />
  );
}

export const PInfo: React.FC<PInfoProps> = ({ message, position, id, media }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState(position.left);
  // A hint layer raised above the default rung paints the bubble there (an
  // inline z wins over the class); outside one, today's class alone.
  const layerZ = useTooltipZ();

  // Keep the bubble inside the viewport: anchoring at `position.left` pushes the
  // right edge off-screen when the trigger sits near the right edge (e.g. the
  // right-most toolbar icon). Slide it left just enough to fit. useLayoutEffect
  // runs before paint, so there's no visible jump.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const width = el.offsetWidth;
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    let next = position.left;
    if (next > maxLeft) next = maxLeft;
    if (next < VIEWPORT_MARGIN) next = VIEWPORT_MARGIN;
    setLeft(next);
  }, [position.left, position.top, message]);

  // When the bubble had to be shifted to stay on-screen the arrow would no
  // longer point at the trigger, so hide it rather than point at empty space.
  const shifted = Math.round(left) !== Math.round(position.left);
  const arrowStyles =
    position.showAbove === false
      ? "absolute left-4 top-0 transform -translate-y-1/2 rotate-45 w-2 h-2 surface-primary border-t border-l outline-quaternary"
      : "absolute left-32 bottom-0 transform translate-y-1/2 rotate-45 w-2 h-2 surface-primary border-b border-r outline-quaternary";

  // Portalled to <body>: the bubble is position:fixed, so its place in the
  // tree never mattered for layout — but callers now render it inside a <ul>
  // of drag items, where a stray <div> is invalid DOM.
  return createPortal(
    // The bubble swallows its own presses: it is portalled to <body>, but
    // React events still travel the COMPONENT tree — a tap in it reached the
    // seating-export row it hangs off and flipped that option.
    <div
      ref={ref}
      id={id}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      // z-[10001]: above the AI drawer/rail (z-10000) — tooltips triggered
      // from INSIDE the drawer header were buried behind it at the old
      // z-9999 — but still below toasts (z-10002). A tooltip only renders
      // while its trigger is actively hovered, so it can't linger over
      // unrelated overlays.
      // `w-max`: the bubble's width is its text's (up to max-w-80), never
      // the room left of the anchor — a fixed box anchored near the right
      // edge shrank to that room, was measured that narrow, and the clamp
      // above then parked it flush against the viewport edge (SMS manual
      // send, screens r1 D7).
      className="z-[10001] fixed w-max px-3 py-2 max-w-80 surface-primary border rounded-lg outline-quaternary"
      style={{
        ...(layerZ !== TOOLTIP_Z_DEFAULT ? { zIndex: layerZ } : {}),
        minWidth: "150px",
        ...(position.showAbove
          ? { bottom: `${position.top + 8}px` } // Use bottom instead of top
          : { top: `${position.top + 8}px` }),
        left: `${left}px`,
      }}
    >
      {/* Above the icon/text row, not in it: 288px + px-3 fits max-w-80. */}
      {media && <BubbleMedia media={media} />}
      <div className="flex items-start gap-2 font-secondary">
        <IconInfoCircle className="block shrink-0 mt-0.5" size={12} />
        <span className="body-3-regular whitespace-pre-line">{message}</span>
      </div>
      {!shifted && <div className={arrowStyles}></div>}
    </div>,
    document.body,
  );
};
